import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next'; // Added for translations

import { capitalizeFirstLetter, truncateString } from 'utility/functions';
import { IconHelper } from 'components/generic/IconHelper';
import { useInlineAsset } from '../context';

import InlineAssetStatus from '../containers/InlineAssetStatus';

import { StatusChip } from 'components/Search/components/fields/generic/StatusChip';
import InlineAssetLink from '../containers/InlineAssetLink';

const InlineAssetDisplay = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const { inlineProps, inlineRecord, isLoading, assetConfig, isRelatedContent } =
    useInlineAsset();
  const theme = useTheme();
  const gridColumns =
    inlineRecord?.title || inlineRecord?.description ? '1 / span 2' : '2';
  const placeholder = inlineProps.node.attrs.placeholder;

  // Get override values from node attributes
  const overrideTitle = inlineProps.node.attrs['override-title'];
  const overrideByline = inlineProps.node.attrs['override-byline'];
  const overrideContent = inlineProps.node.attrs['override-content'];

  // Use override values if present, otherwise fall back to original values
  const title =
    overrideTitle ||
    inlineRecord?.title ||
    inlineRecord?.style ||
    capitalizeFirstLetter(inlineProps?.node?.attrs?.type);
  const description =
    overrideContent ||
    inlineRecord?.summary ||
    inlineRecord?.description ||
    inlineRecord?.content;
  const byline = overrideByline || inlineRecord?.byline;
  const assetRemoved = Boolean(inlineProps?.node?.attrs?.asset?.type === 'removed');
  const width = inlineProps.node.attrs.width || 'default';
  const alignment = inlineProps.node.attrs.alignment || 'default';
  const presentation = inlineProps.node.attrs.presentation || 'default';

  if (isLoading) {
    return null;
  }

  return (
    <Box
      data-testid="inline-asset-display"
      className="inline-asset-display"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 1.5,
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <IconHelper iconType={inlineRecord?.type || inlineProps.node.attrs.type} />
      {(title || description) && (
        <Box
          data-testid="inline-asset-title"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {(title || assetConfig?.title) && (
            <Box
              contentEditable={false}
              sx={{ fontSize: '1rem' }}
            >
              {assetConfig?.title && !placeholder ? (
                assetConfig.title(inlineRecord)
              ) : (
                <>
                  {truncateString(title, 150)}{' '}
                  {(placeholder || assetConfig?.noEdit) &&
                    !assetRemoved &&
                    t('blox.editorial.inline.placeholder', 'Placeholder')}
                </>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isRelatedContent && inlineRecord && inlineRecord.uuid && (
              <StatusChip
                record={inlineRecord}
                chipVariant="filled"
                iconSize={16}
              />
            )}
            <InlineAssetStatus />
            <InlineAssetLink />
          </Box>
        </Box>
      )}
      {!placeholder && assetConfig?.display && (
        <Box
          data-testid="inline-asset-content"
          draggable={false}
          sx={{
            gridColumn: gridColumns,
          }}
        >
          {assetConfig?.display(inlineRecord, theme)}
        </Box>
      )}

      {assetConfig?.showContent && description && (
        <Box
          contentEditable={false}
          sx={{
            fontSize: '0.85rem',
            fontWeight: '300',
            textTransform: 'none',
            gridColumn: gridColumns,
            '& div > p': {
              my: 'unset !important',
            },
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(truncateString(description, 150)),
            }}
          />
        </Box>
      )}

      {assetConfig?.showByline && byline && (
        <Box
          contentEditable={false}
          sx={{
            fontSize: '0.75rem',
            fontWeight: '300',
            textTransform: 'none',
            gridColumn: gridColumns,
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(byline),
            }}
          />
        </Box>
      )}

      {alignment !== 'default' || width !== 'default' || presentation !== 'default' ? (
        <Box
          contentEditable={false}
          sx={{
            fontSize: '0.75rem',
            fontWeight: '300',
            textTransform: 'none',
            gridColumn: gridColumns,
            display: 'flex',
            justifyContent: 'end',
            bottom: 0,
            '& span:not(:last-of-type)::after': {
              content: '"|"',
              margin: '0 0.5rem',
            },
          }}
        >
          {alignment !== 'default' && (
            <span>
              {t('blox.editorial.inline.align', 'Align')}:{' '}
              {t(
                `blox.editorial.inline.displayProperties.alignment.${alignment}`,
                capitalizeFirstLetter(alignment),
              )}
            </span>
          )}
          {width !== 'default' && (
            <span>
              {t('blox.editorial.inline.width', 'Width')}:{' '}
              {t(
                `blox.editorial.inline.displayProperties.width.${width}`,
                capitalizeFirstLetter(width),
              )}
            </span>
          )}
          {presentation !== 'default' && (
            <span>
              {t('blox.editorial.inline.presentation', 'Presentation')}:{' '}
              {t(
                `blox.editorial.inline.displayProperties.presentation.${presentation}`,
                capitalizeFirstLetter(presentation),
              )}
            </span>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default InlineAssetDisplay;
